import * as Y from 'yjs';
import { Collaboration } from '@pncl/common-models';
import { matchI } from 'ts-adt';
import { BehaviorSubject, Subject, throttleTime } from 'rxjs';
import { PencilSocket } from '../socket-utilities/pencil-socket';
import { PendingStructsManager } from './PendingStructsManager';
import SyncProvider = Collaboration.SyncProvider;
import ADTUtil = Collaboration.Utils.ADTUtil;

export class ClientSyncProvider {
  /**
   * constant reference to the update handler
   * @param update
   * @param origin
   */
  private readonly _updateHandler = (update: Uint8Array, origin: unknown) => {
    if (origin !== this) {
      this._sendMessageToServer({ _type: 'Update', scope: this.scope, update });
    }
  };

  /**
   * Is the sync provider bound to a document
   */
  private _subscribed = false;

  /**
   * Has the sync message been resolved
   */
  private _synced = new BehaviorSubject(false);
  public synced = this._synced.asObservable();

  private readonly _pendingStructs$ = new Subject();

  /**
   * buffer to store updates that are received while syncing is occurring
   */
  private _updateBuffer: ArrayBuffer[] = [];

  private readonly pendingStructsManager: PendingStructsManager;
  private firstDocSyncDone = false;

  constructor(
    private socket: PencilSocket<string>,
    private doc: Y.Doc,
    private scope: SyncProvider.Scope,
    pendingStructsSyncThreshold?: number,
  ) {
    this.pendingStructsManager = new PendingStructsManager(pendingStructsSyncThreshold);

    socket.on(SyncProvider.EventName, (_res) => {
      const res = _res as SyncProvider.Response;
      if (!this._validateScope(res.scope)) {
        return;
      }
      matchI(res)({
        Subscribe: () => {
          // Subscribe responses should only occur in a callback
          throw new Error('Unexpected Subscribe');
        },
        Unsubscribe: (unsubscribe) => this._handleUnsubscribe(unsubscribe),
        Update: (update) => this._handleUpdate(update),
        Sync: (sync) => this._handleSync(sync),
      });
    });

    // When the connect event is fired, all rooms in socket.io have been left
    // we are going to join the rooms and sync again
    socket.on('connect', () => {
      if (this._subscribed) {
        this._subscribe();
      }
    });

    // When we reconnect we did not lose the session with the server
    // but events were dropped. When we reconnect we should sync
    socket.on('reconnect', () => {
      if (this._subscribed) {
        this._initiateSync();
      }
    });

    // check for pending structs and if they persist we should
    // then sync to get the server and the client up to date
    this.doc.on('afterTransaction', (trans): void => {
      if (
        trans.doc.store.pendingStructs &&
        this.pendingStructsManager.shouldSyncMissingUpdates(trans.doc.store.pendingStructs.missing)
      ) {
        this._pendingStructs$.next(true);
      } else {
        this._pendingStructs$.next(false);
      }
    });

    // If there are pending structs sync with the server
    this._pendingStructs$.pipe(throttleTime(1000)).subscribe((pendingStructs) => {
      if (pendingStructs && this._subscribed) {
        this._initiateSync();
      }
    });
  }

  // ---- PUBLIC METHODS ----

  /**
   * Binds the provider to the Y.Doc for the scope
   */
  public subscribe(): void {
    if (this._subscribed) {
      return;
    }

    this._subscribe();
  }

  /**
   * Unbinds the provider from the Y.Doc and scope
   */
  public unsubscribe(): void {
    this._sendMessageToServer(
      {
        _type: 'Unsubscribe',
        scope: this.scope,
      },
      (res) => {
        this._handleUnsubscribe(res);
      },
    );
    this.doc.off('update', this._updateHandler);
  }

  /**
   * Intiates a sync with the server which will fetch any data the
   * client is missing and publish any data the server is missing
   */
  public sync(): void {
    this._initiateSync();
  }

  // ---- PRIVATE METHODS ----

  /**
   * Sends the initial subscribe message to the server
   */
  private _subscribe(): void {
    this._sendMessageToServer(
      {
        _type: 'Subscribe',
        scope: this.scope,
      },
      (res) => {
        this._handleSubscribe(res);
      },
    );
  }

  /**
   * Initiate a sync with the server
   */
  private _initiateSync(): void {
    this._synced.next(false);

    const request: ADTUtil.ExtractADT<SyncProvider.Request, 'Sync'> = {
      _type: 'Sync',
      clientState: Y.encodeStateVector(this.doc),
      scope: this.scope,
    };
    this._sendMessageToServer(request);
  }

  /**
   * Handle the subscribe response from the server
   * @param res
   */
  private _handleSubscribe(res: ADTUtil.ExtractADT<SyncProvider.Response, 'Subscribe'>) {
    if (res.Response._type === 'Error') {
      throw new Error(`Error Subscribing to scope\n${res.Response.msg}`);
    }
    this._subscribed = true;

    // Catch the client up to whatever state the server has
    this._initiateSync();

    // Listen for updates on the bound Y.Doc
    this.doc.on('update', this._updateHandler);
  }

  /**
   * Handle unsubscribe response messages from the server
   * @param res
   */
  private _handleUnsubscribe(res: ADTUtil.ExtractADT<SyncProvider.Response, 'Unsubscribe'>) {
    if (res.Response._type === 'Error') {
      throw new Error(`Error Unsubscribing to scope\n${res.Response.msg}`);
    }
    this._subscribed = false;
  }

  /**
   * Apply an update message to local Y.Doc
   * @param res
   * @returns
   */
  private _handleUpdate(res: ADTUtil.ExtractADT<SyncProvider.Response, 'Update'>) {
    if (res.Response._type === 'Error') {
      throw new Error(`Error sending update to scope\n${res.Response.msg}`);
    }

    if (!this._synced.getValue()) {
      // buffer messages. If we apply updates before receiving a sync then consumers listening to the
      // doc.on("update") event will try to read the yDoc before it has the full state. This may lead to
      // errors, especially for Mario as Mario clients tend to automatically set the state to an initial state
      // if an invalid state is received.
      this._updateBuffer.push(res.Response.update);
    } else {
      Y.applyUpdate(this.doc, new Uint8Array(res.Response.update), this);
    }
  }

  /**
   * Handle sync messages to from the server
   * @param res
   */
  private _handleSync(res: ADTUtil.ExtractADT<SyncProvider.Response, 'Sync'>): void {
    if (res.Response._type === 'Error') {
      throw new Error(`Error Syncing to scope\n${res.Response.msg}`);
    }
    // Apply the update provided in the sync
    if (res.Response.update || this._updateBuffer.length > 0) {
      const updateU8 = new Uint8Array(res.Response.update ?? []);

      const mergedUpdates = Y.mergeUpdates([
        updateU8,
        ...this._updateBuffer.map((n) => new Uint8Array(n)),
      ]);
      this.doc.transact(() => {
        Y.applyUpdate(this.doc, mergedUpdates, this);
        if (this.doc.store.pendingStructs && !this.firstDocSyncDone) {
          this.pendingStructsManager.updateFirstLoadPendingStructs(
            this.doc.store.pendingStructs.missing,
          );
        }
      });
    }
    this._updateBuffer = [];

    this.firstDocSyncDone = true;
    this._synced.next(true);

    // Send any missing data the client has to the server
    const update = Y.encodeStateAsUpdate(this.doc, new Uint8Array(res.Response.serverState));

    this._sendMessageToServer({
      _type: 'Update',
      scope: this.scope,
      update,
    });
  }

  /**
   * Send a message to the server and validate types
   *
   * @param req
   * @param cb
   */
  private _sendMessageToServer<Req extends SyncProvider.Request>(
    req: Req,
    cb?: (res: Extract<SyncProvider.Response, Pick<Req, '_type'>>) => void,
  ) {
    if (cb) {
      this.socket.emit(SyncProvider.EventName, req, (res: SyncProvider.Response) => {
        if (res._type !== req._type) {
          throw new Error(
            `Response from the server is of type ${res._type}, expected ${req._type}`,
          );
        }
        cb(res as Extract<SyncProvider.Response, Pick<Req, '_type'>>);
      });
    } else {
      this.socket.emit(SyncProvider.EventName, req);
    }
  }

  private _validateScope(scope: SyncProvider.Scope): boolean {
    return this._getUIDForScope(scope) === this._getUIDForScope(this.scope);
  }

  private _getUIDForScope(scope: SyncProvider.Scope): string {
    return matchI(scope)({
      Space: ({ id }) => id,
      Mario: ({ marioId, spaceId }) => `${spaceId}-${marioId}`,
    });
  }
}
